import { useState } from "react";
import { z } from "zod";

import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { UserOrigin } from "@/hooks/queries/org-admin-dashboard-types";
import { useChangeClinicianEmail } from "@/hooks/queries/useOrgMemberActions";
import {
  toMemberActionError,
  type MemberActionBanner,
} from "@/routes/org-admin/member-actions-error";

// A DEDICATED dialog for the email correction (Story 14.4, night boundary #6),
// not an inline field on the Edit-member modal: the address is the clinician's
// sign-in identifier AND (for a LearnWorlds account) the course-matching key, so
// it gets its own explicit confirm step rather than riding the Save-changes
// dirty check with three display labels.

/**
 * AC6 — shown BEFORE submit for a `learnworlds_new` row only. The address is
 * what LearnWorlds matches on (Begin-Calibration attach, renewal attribution),
 * so a change made only here silently splits the two records. The change itself
 * is never blocked; mismatches land in the existing manual-review paths.
 */
export const LEARNWORLDS_EMAIL_WARNING =
  "This clinician's account is matched to LearnWorlds by email address. Changing it here does not change it on LearnWorlds — ask them to use the new address there too, or their course progress won't attach.";

/**
 * Client-side gate for the common typo (`not-an-email`) — same copy as the
 * Add-person form's email rule. Checked on submit, not per keystroke, so the
 * admin is never scolded mid-typing. The server stays the authority for the
 * narrower sign-in grammar: an RFC-valid address it refuses answers a coded
 * 422 ORG-015 with its own banner. Without this gate an uncoded 422 from
 * `EmailStr` fell through to the generic "review the values you entered",
 * which points at the wrong form (PR #688 review, round 2).
 */
export const EMAIL_VALIDATION_MESSAGE = "Enter a valid work email address.";

const emailShape = z.string().email();

export function ChangeEmailDialog({
  userId,
  currentEmail,
  userOrigin,
  onCancel,
  onChanged,
}: {
  userId: string;
  currentEmail: string;
  userOrigin: UserOrigin;
  onCancel: () => void;
  /** Called with the address the SERVER confirmed, never the typed draft. */
  onChanged: (newEmail: string) => void;
}) {
  const [newEmail, setNewEmail] = useState("");
  const [banner, setBanner] = useState<MemberActionBanner | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const changeEmail = useChangeClinicianEmail(userId);

  const trimmed = newEmail.trim();
  const canSubmit = trimmed.length > 0 && !changeEmail.isPending;

  function onConfirm() {
    if (!emailShape.safeParse(trimmed).success) {
      setValidationError(EMAIL_VALIDATION_MESSAGE);
      return;
    }
    setBanner(null);
    changeEmail.mutate(
      { new_email: trimmed },
      {
        onSuccess: (data) => onChanged(data.new_email),
        onError: (error) => setBanner(toMemberActionError(error)),
      },
    );
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="w-[min(var(--width-md,520px),100%)]">
        <DialogHeader>
          <DialogTitle>Change email</DialogTitle>
          <DialogDescription>
            {`The new address becomes this clinician's sign-in email immediately. A notification is sent to ${currentEmail}.`}
          </DialogDescription>
        </DialogHeader>

        {userOrigin === "learnworlds_new" ? (
          <Banner variant="warn" title="Also update it on LearnWorlds">
            {LEARNWORLDS_EMAIL_WARNING}
          </Banner>
        ) : null}

        <div className="flex flex-col gap-1">
          <Label htmlFor="change-email-new-email">New work email</Label>
          <Input
            id="change-email-new-email"
            type="email"
            autoComplete="off"
            value={newEmail}
            aria-invalid={validationError ? true : undefined}
            aria-describedby={
              validationError ? "change-email-validation-error" : undefined
            }
            onChange={(e) => {
              setNewEmail(e.target.value);
              setValidationError(null);
            }}
          />
          {validationError ? (
            <p
              id="change-email-validation-error"
              role="alert"
              className="text-sm text-[color:var(--text-error)]"
            >
              {validationError}
            </p>
          ) : null}
        </div>

        {banner ? (
          <Banner variant={banner.variant} title={banner.title}>
            {banner.body}
          </Banner>
        ) : null}

        <DialogFooter>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={!canSubmit}>
            Change email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
